// src/features/quote/QuoteModal.jsx
import React, { useState } from "react";
/**
 * Props:
 * - open, onClose
 * - svc, customer, produkt
 * - serviceLines, partLines, totals
 * - workDesc, quoteValidUntil
 * - checklistSummary (valgfritt)
 * - fmtNOK, fmtDT, fmtDate, fmtMin (sendes inn for å unngå duplikat)
 * - onSendEmail?: () => void | Promise<void>   (valgfri)
 * - sendingEmail?: boolean                      (valgfri)
 */
export default function QuoteModal(props) {
  const {
    open,
    onClose,
    svc,
    customer,
    produkt,
    serviceLines = [],
    partLines = [],
    totals = { arbeid: 0, deler: 0, total: 0 },
    workDesc = "",
    quoteValidUntil = null,
    checklistSummary = [],
    fmtNOK,
    fmtDT,
    fmtDate,
    fmtMin,
    onSendEmail,
    sendingEmail = false,
  } = props;

  const [emailStatus, setEmailStatus] = useState(null); // "ok" | "error" | null
  const hasEmail = !!(customer?.email);

  // 👇 NY: er pristilbud aktivert på servicen?
  const quoteEnabled = svc?.quote?.enabled === true;


  if (!open) return null;

  const handleSendEmail = async () => {
    if (!onSendEmail || sendingEmail || !hasEmail || !quoteEnabled) return;
    setEmailStatus(null);
    try {
      await onSendEmail();
      setEmailStatus("ok");
    } catch (e) {
      console.error("Feil ved sending av pristilbud på e-post:", e);
      setEmailStatus("error");
    }
  };

  const hasAnyDiscount = partLines.some(
    (p) => Number(p.discount) > 0
  );


  return (
    <div
      id="quote-print-root"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg max-w-[96vw] max-h-[95vh] overflow-auto print:p-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* === STYLE BLOKKEN: behold som før === */}
        <style>
          {`
@media print {
  @page { size: A4; margin: 12mm; }
  .printing-quote body > *:not(.app-screen) { display: none !important; }
  .printing-quote .app-screen > *:not(#quote-print-root) { display: none !important; }
  .printing-quote .app-screen { background: transparent !important; box-shadow:none!important; border:0!important; border-radius:0!important; padding:0!important; margin:0!important; }
  .printing-quote #quote-print-root { display:block!important; position:static!important; inset:auto!important; margin:0!important; padding:0!important; background:transparent!important; z-index:auto!important; }
  .printing-quote #quote-print-root > div { position:static!important; box-shadow:none!important; background:transparent!important; max-width:none!important; max-height:none!important; overflow:visible!important; padding:0!important; margin:0!important; }
  #quote-paper {
    width: 200mm !important;        /* litt smalere enn printbar bredde */
    margin: 0 auto !important;
    padding: 0 4mm !important;      /* liten innvendig høyre/venstre-marg */
    box-sizing: border-box !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    outline: none !important;
  }  .printing-quote nav, .printing-quote header, .printing-quote #stickyHeader, .printing-quote .no-print { display:none !important; }
  .tbl, .checklist-compact table { page-break-inside: avoid; }
  #quote-print-area { min-height:auto !important; }
  html, body { margin:0!important; padding:0!important; }
}
#quote-print-area { width:100%; min-height:297mm; box-sizing:border-box; font-size:12px; }
#quote-print-area .row { display:flex; gap:12px; }
#quote-print-area .col { flex:1; }
#quote-print-area .tbl { width:100%; border-collapse:collapse; }
#quote-print-area .tbl th, #quote-print-area .tbl td { border-bottom:1px solid #e5e7eb; padding:6px 4px; text-align:left; vertical-align:top; }
#quote-print-area .tbl th { background:#f8fafc; font-weight:600; }
#quote-print-area .right { text-align:right; }
#quote-print-area .muted { color:#6b7280; }
#quote-print-area .section-title { font-weight:600; margin:16px 0 8px; font-size:14px; }
#quote-print-area .h-line { height:1px; background:#e5e7eb; margin:6px 0 12px; }
.print-break-before { display:none; }
@media print {
  .print-break-before { display:block !important; break-before:page !important; page-break-before:always !important; height:0; }
  .print-avoid-break { break-inside:avoid; page-break-inside:avoid; }
}
`}
        </style>

        {/* Skjerm-header */}
        <div className="no-print flex items-center justify-between px-6 pt-5 pb-3">
          <h4 className="text-lg font-semibold">Pristilbud (for utskrift)</h4>
          <div className="flex items-center gap-3">
            {onSendEmail && (
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !hasEmail || !quoteEnabled}
                title={
                  !hasEmail
                    ? "Kunden mangler e-postadresse"
                    : !quoteEnabled
                      ? "Pristilbudet må være aktivert før du kan sende på e-post."
                      : "Send pristilbud som PDF på e-post"
                }
                className={`px-3 py-1 rounded text-white ${sendingEmail || !hasEmail || !quoteEnabled
                  ? "bg-emerald-400 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
              >
                {sendingEmail ? "Sender e-post…" : "Send på e-post"}
              </button>
            )}


            {!hasEmail && (
              <span className="no-print text-xs text-red-600">
                Kan ikke sende pristilbud: Kunden mangler e-postadresse.
              </span>
            )}
            {emailStatus === "ok" && hasEmail && !sendingEmail && (
              <span className="no-print text-xs text-emerald-700">
                Pristilbud er sendt på e-post.
              </span>
            )}
            {emailStatus === "error" && (
              <span className="no-print text-xs text-red-600">
                Klarte ikke å sende e-post. Prøv igjen.
              </span>
            )}


            <button
              onClick={() => window.print()}
              className="px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Skriv ut
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1 rounded border"
            >
              Lukk
            </button>
          </div>
        </div>

        {/* Papiret */}
        <div
          id="quote-paper"
          className="bg-white w-[200mm] md:w-[205mm] lg:w-[210mm] p-6 md:p-8 my-6 mx-auto"
        >
          <div id="quote-print-area">
            {/* Topp */}
            <div
              className="row"
              style={{ alignItems: "flex-start", marginBottom: 12 }}
            >
              <div className="col">
                <div style={{ fontSize: 18, fontWeight: 700 }}>Pristilbud</div>
                <div className="muted">Dato: {fmtDT(new Date())}</div>
                {quoteValidUntil && (
                  <div className="muted">
                    Gyldig til: {fmtDate(quoteValidUntil)}
                  </div>
                )}
              </div>
              <div className="col" style={{ textAlign: "right" }}>
                <div>
                  <strong>Referanse</strong>{" "}
                  {svc?.serviceRef ? (
                    <span className="font-mono">#{svc.serviceRef}</span>
                  ) : (
                    "—"
                  )}
                </div>
                {produkt?.butikk ? (
                  <div className="muted">Butikk: {produkt.butikk}</div>
                ) : null}
              </div>
            </div>
            <div className="h-line" />

            {/* Kunde + produkt */}
            <div className="row" style={{ marginBottom: 16 }}>
              <div className="col">
                <div className="section-title">Kunde</div>
                <div>
                  <strong>
                    {customer?.firstName} {customer?.lastName}
                  </strong>
                </div>
                <div className="muted">{customer?.phoneNumber || "—"}</div>
                <div className="muted">{customer?.email || "—"}</div>
              </div>
              <div className="col">
                <div className="section-title">Produkt</div>
                <div>
                  <strong>
                    {produkt?.merke}
                    {produkt?.type ? ` – ${produkt.type}` : ""}
                  </strong>
                </div>
                {produkt?.farge && (
                  <div className="muted">Farge: {produkt.farge}</div>
                )}
              </div>
            </div>

            {/* Foreslått arbeid */}
            <div className="section-title">Foreslått arbeid</div>
            <table className="tbl" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Beskrivelse</th>
                  <th className="right" style={{ width: "22%" }}>
                    Pris
                  </th>
                </tr>
              </thead>
              <tbody>
                {serviceLines.length ? (
                  serviceLines.map((l, i) => (
                    <tr key={l._id || i}>
                      <td>
                        <div>
                          <strong>{l.navn || "Tjeneste"}</strong>
                        </div>
                        {l.kommentar ? (
                          <div className="muted">
                            Merknad: {l.kommentar}
                          </div>
                        ) : null}
                        {l.tidsbruk ? (
                          <div className="muted no-print">
                            Estimert tid: {fmtMin(l.tidsbruk)}
                          </div>
                        ) : null}

                      </td>
                      <td className="right">
                        {l.reklamasjon ? (
                          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                              {fmtNOK(l.pris)}
                            </span>
                            <span style={{ color: "#2563eb", fontWeight: 700, fontSize: "0.8em", background: "#eff6ff", borderRadius: 4, padding: "1px 5px" }}>
                              Reklamasjon
                            </span>
                          </span>
                        ) : l.gratisarbeid ? (
                          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                              {fmtNOK(l.pris)}
                            </span>
                            <span style={{ color: "#7c3aed", fontWeight: 700, fontSize: "0.8em", background: "#f5f3ff", borderRadius: 4, padding: "1px 5px" }}>
                              Gratisarbeid
                            </span>
                          </span>
                        ) : (
                          fmtNOK(l.pris)
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="muted">
                      Ingen foreslåtte tjenester
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td className="right">
                    <strong>Sum arbeid</strong>
                  </td>
                  <td className="right">
                    <strong>{fmtNOK(totals.arbeid)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>

            {/* Deler (estimat) */}
            <div className="section-title">Deler (estimat)</div>
            <table className="tbl" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Del</th>
                  <th className="right" style={{ width: "12%" }}>Antall</th>
                  <th className="right" style={{ width: "14%" }}>Enhetspris</th>

                  {hasAnyDiscount && (
                    <th className="right" style={{ width: "10%" }}>
                      Rabatt
                    </th>
                  )}

                  <th className="right" style={{ width: "16%" }}>Linje</th>
                </tr>
              </thead>

              <tbody>
                {partLines.length ? (
                  partLines.map((p, i) => {
                    const price = Number(p.price) || 0;
                    const qty = Math.max(1, Number(p.qty) || 1);
                    const subtotal = price * qty;
                    const discountKr = Number(p.discount) || 0;
                    const lineTotal = Math.max(0, subtotal - discountKr);

                    return (
                      <tr key={`${i}-${p.ean || p.product || "part"}`}>
                        <td>
                          <strong>{p.product || "Del"}</strong>
                        </td>
                        <td className="right">{qty}</td>
                        <td className="right">{fmtNOK(price)}</td>

                        {hasAnyDiscount && (
                          <td className="right">
                            {discountKr > 0 ? fmtNOK(discountKr) : ""}
                          </td>
                        )}

                        <td className="right">
                          {p.isWarranty ? (
                            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                              <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                                {fmtNOK(lineTotal)}
                              </span>
                              <span style={{ color: "#2563eb", fontWeight: 700, fontSize: "0.8em", background: "#eff6ff", borderRadius: 4, padding: "1px 5px" }}>
                                Reklamasjon
                              </span>
                            </span>
                          ) : (
                            fmtNOK(lineTotal)
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={hasAnyDiscount ? 5 : 4} className="muted">
                      Ingen deler i estimatet
                    </td>
                  </tr>
                )}
              </tbody>


              <tfoot>
                <tr>
                  <td colSpan={hasAnyDiscount ? 4 : 3} className="right">
                    <strong>Sum deler</strong>
                  </td>
                  <td className="right">
                    <strong>{fmtNOK(totals.deler)}</strong>
                  </td>
                </tr>
              </tfoot>


            </table>

            {/* Kommentar */}
            <div className="section-title">Kommentar</div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 8,
                minHeight: 56,
              }}
            >
              {(workDesc || "").trim() || (
                <span className="muted">
                  Kort beskrivelse av hva tilbudet omfatter …
                </span>
              )}
            </div>

            {/* Total */}
            <div className="row" style={{ marginTop: 16 }}>
              <div className="col" />
              <div
                className="col"
                style={{ maxWidth: 340, marginLeft: "auto" }}
              >
                <table className="tbl">
                  <tbody>
                    <tr>
                      <td className="right">
                        <strong>Estimert total</strong>
                      </td>
                      <td
                        className="right"
                        style={{ width: "40%" }}
                      >
                        <strong>{fmtNOK(totals.total)}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="muted" style={{ marginTop: 6 }}>
                  Dette er et pristilbud. Arbeid igangsettes først når
                  tilbudet er godkjent.
                </div>
                {quoteValidUntil && (
                  <div className="muted">
                    Tilbudet er gyldig til {fmtDate(quoteValidUntil)}.
                  </div>
                )}
              </div>
            </div>

            {/* Valgfritt: Sjekkliste som grunnlag */}
            {Array.isArray(checklistSummary) &&
              checklistSummary.length > 0 && (
                <>
                  <div className="print-break-before" />
                  <div
                    className="section-title"
                    style={{ marginTop: 8 }}
                  >
                    Kontrollpunkter (grunnlag)
                  </div>
                  <div className="checklist-compact print-avoid-break">
                    <table
                      className="tbl tbl--compact"
                      style={{ marginBottom: 8 }}
                    >
                      <thead>
                        <tr>
                          <th>Punkt</th>
                          <th style={{ width: "22%" }}>
                            Arbeid
                          </th>
                          <th>Merknad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklistSummary.map((row) => {
                          const supportsDeviation =
                            row.canDeviation !== false; // default true
                          const hasDeviation =
                            supportsDeviation && !!row.deviation;

                          let vurdering = "—";

                          if (row.ok) {
                            vurdering = "OK";
                          } else if (row.done && !hasDeviation) {
                            vurdering = "Utføres"; // endret fra Utføres
                          } else if (hasDeviation) {
                            vurdering = row.done
                              ? "Avvik – utbedres"
                              : "Avvik – utbedres ikke";
                          }


                          return (
                            <tr key={row.slug}>
                              <td>{row.title}</td>
                              <td>{vurdering}</td>
                              <td className="muted">
                                {row.comment || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
